import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, LogIn } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Loader';

const AdminLogin = () => {
  const { isAuthenticated, loading, loginWithCredentials } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/admin');
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return <Loader fullScreen />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    const result = await loginWithCredentials(username.trim(), password);

    if (result.success) {
      navigate('/admin');
    } else {
      setError(result.message);
    }

    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-violet-600/20 mb-4">
            <Shield className="w-8 h-8 text-violet-400" />
          </div>
          <h1 className="font-heading font-bold text-2xl uppercase tracking-wider gradient-text">
            Administration
          </h1>
          <p className="text-gray-400 mt-2">
            Connectez-vous avec votre identifiant administrateur
          </p>
        </div>

        {/* Login Card */}
        <div className="glass rounded-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Nom d'utilisateur</label>
              <input
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white outline-none focus:border-violet-500"
                autoComplete="username"
                data-testid="admin-username-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white outline-none focus:border-violet-500"
                autoComplete="current-password"
                data-testid="admin-password-input"
                required
              />
            </div>
            {error ? (
              <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-3 bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wider py-4 rounded-lg transition-all hover:shadow-[0_0_20px_rgba(139,92,246,0.5)]"
              data-testid="admin-login-btn"
            >
              <LogIn className="w-5 h-5" />
              {submitting ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>

        {/* Info */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Seuls les administrateurs autorisés peuvent accéder au panel.
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
